import { StrictMode, useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createCasaLeon3DExperience,
  type CasaLeon3DInteractiveFocus,
  type CasaLeon3DPerfStats,
  type CasaLeon3DSimPanels,
} from '@casa-leon-vivo/engine';
import './vivo-ui.css';
import { UI } from './ui-strings.js';
import {
  DemoIntegralSession,
  INTEGRAL_SCENARIOS,
  resetIntegralDemoSeq,
} from '@casa-leon-vivo/scenario';
import { DynamicFloorEngine } from './floor/dynamic-floor.js';
import { runScenario006, SCENARIO_006_META, type Scenario006Id } from './floor/demo-scenarios-006.js';
import { deriveWeatherVisual } from './weather/derive-visual.js';
import { loadWeather } from './weather/weather-service.js';
import type { WeatherViewModel } from './weather/weather-types.js';
import { wmoLabelEs } from './weather/wmo-labels.js';
import { PanelWeather } from './ui/PanelWeather.js';
import { GuestSimulation } from './guests/guest-simulation.js';
import { TableLayoutEditor } from './editor/table-layout-editor.js';
import type { VivoAppMode } from '@casa-leon-vivo/engine';

function layoutTablePositions(floor: DynamicFloorEngine): Map<number, { x: number; z: number }> {
  const map = new Map<number, { x: number; z: number }>();
  for (const m of floor.getLayout()) {
    map.set(m.logicalTableId, { x: m.worldX, z: m.worldZ });
  }
  return map;
}

function PanelKitchen({ orders }: { orders: CasaLeon3DSimPanels['kitchenOrders'] }) {
  return (
    <div className="cl-panel">
      <h3>{UI.panelKitchen}</h3>
      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        {orders.map((o) => (
          <li key={o.id}>
            <b>{o.table}</b> � {o.items.join(', ')} <em>({o.status}, ETA {o.etaMin}m)</em>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PanelWhatsApp({ msgs }: { msgs: CasaLeon3DSimPanels['whatsapp'] }) {
  return (
    <div className="cl-panel">
      <h3>{UI.panelWhatsApp}</h3>
      {msgs.map((m, i) => (
        <div
          key={i}
          style={{
            marginTop: 8,
            padding: 8,
            borderRadius: 8,
            background: m.bot ? 'rgba(37, 211, 102, 0.15)' : 'rgba(255,255,255,0.06)',
          }}
        >
          <div style={{ fontSize: 11, opacity: 0.75 }}>{m.at} � {m.from}</div>
          {m.text}
        </div>
      ))}
    </div>
  );
}

function PanelPhone({ phone }: { phone: CasaLeon3DSimPanels['phone'] }) {
  return (
    <div className="cl-panel">
      <h3>{UI.panelPhone}</h3>
      <p style={{ margin: '8px 0 0' }}>
        {phone.caller} � {phone.reason}
        <br />
        {UI.state}: <b>{phone.state}</b>
      </p>
    </div>
  );
}

function PanelRegister({ reg }: { reg: CasaLeon3DSimPanels['register'] }) {
  return (
    <div className="cl-panel">
      <h3>{UI.panelRegister}</h3>
      <p>
        Caja {reg.open ? UI.cashOpen : UI.cashClosed} � {UI.salesToday}: $
        {reg.salesToday.toLocaleString('es-AR')}
      </p>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {reg.queue.map((q, i) => (
          <li key={i}>{q.table}: ${q.total.toLocaleString('es-AR')} ({q.method})</li>
        ))}
      </ul>
    </div>
  );
}

function PanelTable({ focus }: { focus: CasaLeon3DInteractiveFocus }) {
  const id = focus.id.replace('table-', '');
  return (
    <div className="cl-panel">
      <h3>{UI.panelTable(id)}</h3>
      <p style={{ margin: '8px 0 0' }}>
        {UI.zone}: {focus.zone}. {UI.tableHelp}
      </p>
    </div>
  );
}

function App() {
  const mountRef = useRef<HTMLDivElement>(null);
  const expRef = useRef<ReturnType<typeof createCasaLeon3DExperience> | null>(null);
  const sessionRef = useRef(new DemoIntegralSession());
  const floorRef = useRef(new DynamicFloorEngine());
  const guestSimRef = useRef(new GuestSimulation());
  const layoutEditorRef = useRef(new TableLayoutEditor());
  const appModeRef = useRef<VivoAppMode>('simulation');
  const [appMode, setAppMode] = useState<VivoAppMode>('simulation');
  const [editorSavedAt, setEditorSavedAt] = useState<string | null>(null);
  const [floorMsg, setFloorMsg] = useState(floorRef.current.getState().message);
  const [focus, setFocus] = useState<CasaLeon3DInteractiveFocus | null>(null);
  const [panels, setPanels] = useState<CasaLeon3DSimPanels | null>(null);
  const [audioOn, setAudioOn] = useState(false);
  const [sidebar, setSidebar] = useState(true);
  const [perf, setPerf] = useState<CasaLeon3DPerfStats | null>(null);
  const [charsReady, setCharsReady] = useState(false);
  const [weather, setWeather] = useState<WeatherViewModel>({
    status: 'unavailable',
    bundle: null,
    ageMinutes: null,
    errorMessage: null,
  });
  const [weatherFxOn, setWeatherFxOn] = useState(true);
  const [splash, setSplash] = useState(true);

  const syncEditorToScene = useCallback(() => {
    const layout = floorRef.current.getLayout();
    layoutEditorRef.current.ensureFromLayout(layout);
    expRef.current?.setLayoutEditorState(
      layoutEditorRef.current.getOffsets(),
      layoutEditorRef.current.getDecor(),
    );
  }, []);

  const pushFloor = useCallback(() => {
    const layout = floorRef.current.getLayout();
    expRef.current?.setFloorLayout(layout);
    syncEditorToScene();
    const st = floorRef.current.getState();
    setFloorMsg(st.message + (st.atCapacity ? ' (l\u00edmite)' : ''));
  }, [syncEditorToScene]);

  const pushSnapshot = useCallback(() => {
    expRef.current?.setSnapshot(sessionRef.current.getSnapshot());
    pushFloor();
  }, [pushFloor]);

  useEffect(() => {
    const t = window.setTimeout(() => setSplash(false), 2800);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const pull = async (force = false) => {
      const vm = await loadWeather(force ? { force: true } : undefined);
      if (!cancelled) setWeather(vm);
    };
    void pull();
    const id = window.setInterval(() => void pull(), 15 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (!weather.bundle) return;
    const visual = deriveWeatherVisual(weather.bundle, weatherFxOn);
    expRef.current?.setWeatherVisual(visual);
  }, [weather, weatherFxOn]);

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const exp = createCasaLeon3DExperience(el, {
      onInteractiveFocus: setFocus,
      onSimPanelsUpdate: setPanels,
      onPerfUpdate: setPerf,
      onCharactersReady: () => setCharsReady(true),
      onFrame: (dt) => {
        if (appModeRef.current !== 'simulation') return;
        guestSimRef.current.tick(dt, layoutTablePositions(floorRef.current));
        expRef.current?.setGuestMarkers(guestSimRef.current.getMarkers());
      },
    });
    expRef.current = exp;
    (el as HTMLDivElement & { __vivo3dExp?: typeof exp }).__vivo3dExp = exp;
    exp.setLayoutEditorHandlers({
      snapModule: (moduleId, worldX, worldZ) =>
        floorRef.current.snapModuleToWorld(moduleId, worldX, worldZ),
      onModuleDrop: (moduleId, worldX, worldZ) => {
        layoutEditorRef.current.pushHistory(floorRef.current.exportModules());
        const r = floorRef.current.relocateModule(moduleId, worldX, worldZ);
        if (r.ok) {
          layoutEditorRef.current.clearOffset(moduleId);
          pushFloor();
          syncEditorToScene();
        } else {
          setFloorMsg(`Editor: ${r.error ?? 'no se pudo mover'}`);
        }
      },
    });
    pushSnapshot();
    pushFloor();
    return () => exp.destroy();
  }, [pushSnapshot, pushFloor, syncEditorToScene]);

  const run006 = (id: Scenario006Id) => {
    runScenario006(floorRef.current, id);
    pushFloor();
  };

  const enableAudio = async () => {
    await expRef.current?.unlockAudio();
    setAudioOn(true);
  };

  const runScenario = (key: string) => {
    const fn = INTEGRAL_SCENARIOS[key];
    if (!fn) return;
    resetIntegralDemoSeq();
    sessionRef.current.applyMany(fn());
    pushSnapshot();
  };

  const reset = () => {
    resetIntegralDemoSeq();
    sessionRef.current.reset();
    floorRef.current.reset();
    guestSimRef.current.reset();
    pushSnapshot();
  };

  const setMode = (mode: VivoAppMode) => {
    appModeRef.current = mode;
    setAppMode(mode);
    expRef.current?.setAppMode(mode);
    if (mode === 'edit') {
      expRef.current?.setGuestMarkers([]);
      syncEditorToScene();
    } else {
      pushFloor();
    }
  };

  const spawnGuestParty = (size: number) => {
    const partyId = guestSimRef.current.spawnParty(size);
    const seat = floorRef.current.seatWalkIn(size);
    pushFloor();
    if (seat.ok && seat.tableId != null) {
      const pos = layoutTablePositions(floorRef.current).get(seat.tableId);
      if (pos) guestSimRef.current.assignTable(partyId, seat.tableId, pos);
    }
  };

  const renderFocusPanel = () => {
    if (!focus || !panels) {
      return (
        <div className="cl-panel">
          <h3>{UI.panelInteract}</h3>
          {UI.interactHint}
        </div>
      );
    }
    if (focus.id === 'kitchen-board') return <PanelKitchen orders={panels.kitchenOrders} />;
    if (focus.id === 'whatsapp-sign') return <PanelWhatsApp msgs={panels.whatsapp} />;
    if (focus.id === 'phone') return <PanelPhone phone={panels.phone} />;
    if (focus.id === 'register') return <PanelRegister reg={panels.register} />;
    if (focus.id === 'weather-station') return <PanelWeather weather={weather} />;
    if (focus.id.startsWith('table-')) return <PanelTable focus={focus} />;
    return null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {splash ? (
        <div className="cl-splash" role="presentation">
          <img className="cl-splash-logo" src={`${import.meta.env.BASE_URL}brand/logo.png`} alt="Casa Le�n" />
          <p className="cl-splash-text">{UI.splashWelcome}</p>
        </div>
      ) : null}
      <header className="cl-header">
        <img className="cl-header-logo" src={`${import.meta.env.BASE_URL}brand/logo.png`} alt="" aria-hidden />
        <span className="cl-title">{UI.title}</span>
        <span className="cl-sub">{UI.subtitle}</span>
        <span className="cl-badge">{appMode === 'edit' ? UI.modeEdit : UI.modeSim}</span>
        <span className="cl-badge">{charsReady ? UI.charsReady : UI.charsLoading}</span>
        {!audioOn ? (
          <button type="button" className="cl-btn" onClick={enableAudio} style={{ marginLeft: 'auto' }}>
            {UI.soundOn}
          </button>
        ) : (
          <label className="cl-sub" style={{ marginLeft: 'auto' }}>
            <input
              type="checkbox"
              checked={audioOn}
              onChange={(e) => {
                setAudioOn(e.target.checked);
                expRef.current?.setSoundscapeEnabled(e.target.checked);
              }}
            />
            {UI.soundToggle}
          </label>
        )}
        <button type="button" className="cl-btn" onClick={() => setSidebar((s) => !s)}>
          {sidebar ? UI.hideScenarios : UI.showScenarios}
        </button>
      </header>
      <div style={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}>
        <div
          ref={mountRef}
          data-vivo-mount
          style={{ flex: 1, minWidth: 0, background: 'var(--cl-bg)' }}
        />
        {perf && (
          <div className="cl-hud">
            {weather.bundle ? (
              <span className="cl-hud-weather">
                {Math.round(weather.bundle.current.temperatureC)}�C � {wmoLabelEs(weather.bundle.current.weatherCode)}
                {weather.status === 'stale' ? ' � cache' : ''}
              </span>
            ) : null}
            <span>
              {perf.fps} fps � {perf.frameTimeMs} ms � {(perf.triangles / 1000).toFixed(1)}k tris � DPR {perf.dpr}
            </span>
          </div>
        )}
        <aside
          className="cl-sidebar"
          style={{ width: sidebar ? 280 : 0, padding: sidebar ? 12 : 0, overflow: sidebar ? 'auto' : 'hidden' }}
        >
          <p className="cl-sidebar-help">{UI.cameraHelp}</p>
          <details className="cl-admin-section" open>
            <summary>{UI.sectionMode}</summary>
            <div className="cl-admin-row">
              <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => setMode('simulation')}>
                {UI.modeSim}
              </button>
              <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => setMode('edit')}>
                {UI.modeEdit}
              </button>
            </div>
            {appMode === 'edit' ? (
              <>
                <p className="cl-sim-note">{UI.editorHelp}</p>
                <button
                  type="button"
                  className="cl-btn cl-btn-sidebar"
                  onClick={() => {
                    const prev = layoutEditorRef.current.undo(floorRef.current.exportModules());
                    if (prev) {
                      floorRef.current.applyModules(prev);
                      pushFloor();
                      syncEditorToScene();
                    }
                  }}
                >
                  {UI.editorUndo}
                </button>
                <button
                  type="button"
                  className="cl-btn cl-btn-sidebar"
                  onClick={() => {
                    const next = layoutEditorRef.current.redo(floorRef.current.exportModules());
                    if (next) {
                      floorRef.current.applyModules(next);
                      pushFloor();
                      syncEditorToScene();
                    }
                  }}
                >
                  {UI.editorRedo}
                </button>
                <button
                  type="button"
                  className="cl-btn cl-btn-sidebar"
                  onClick={() => {
                    layoutEditorRef.current.pushHistory(floorRef.current.exportModules());
                    floorRef.current.resetLayoutToDefault();
                    pushFloor();
                    syncEditorToScene();
                  }}
                >
                  {UI.editorResetLayout}
                </button>
                <button
                  type="button"
                  className="cl-btn cl-btn-sidebar"
                  onClick={() => {
                    layoutEditorRef.current.rotateSelected();
                    syncEditorToScene();
                  }}
                >
                  {UI.editorRotate}
                </button>
                <button
                  type="button"
                  className="cl-btn cl-btn-sidebar"
                  onClick={() => {
                    layoutEditorRef.current.duplicateDecorFromSelection(floorRef.current.getLayout());
                    syncEditorToScene();
                  }}
                >
                  {UI.editorDuplicate}
                </button>
                <button
                  type="button"
                  className="cl-btn cl-btn-sidebar"
                  onClick={() => setEditorSavedAt(layoutEditorRef.current.save())}
                >
                  {UI.editorSave}
                </button>
                {editorSavedAt ? (
                  <p className="cl-sim-note">{UI.editorSaved}: {new Date(editorSavedAt).toLocaleString('es-AR')}</p>
                ) : null}
              </>
            ) : null}
          </details>
          <details className="cl-admin-section" open>
            <summary>{UI.sectionCamera}</summary>
            <div className="cl-admin-row">
              <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => expRef.current?.resetCamera()}>
                {UI.resetView}
              </button>
              <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => expRef.current?.setCameraPreset('isometric')}>
                {UI.cameraIso}
              </button>
              <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => expRef.current?.setCameraPreset('salon')}>
                {UI.cameraSalon}
              </button>
              <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => expRef.current?.setCameraPreset('patio')}>
                {UI.cameraPatio}
              </button>
            </div>
          </details>
          <p className="cl-floor-msg">{UI.floorStatus}: {floorMsg}</p>
          <details className="cl-admin-section" open>
            <summary>{UI.sectionGuests}</summary>
            <p className="cl-sim-note">{UI.guestSimNote}</p>
            <button
              type="button"
              className="cl-btn cl-btn-sidebar"
              onClick={() => {
                expRef.current?.setCameraPreset('salon');
                expRef.current?.restartPremiumPilot();
              }}
            >
              {UI.pilotService}
            </button>
            <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => spawnGuestParty(2)}>
              {UI.guestParty2}
            </button>
            <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => spawnGuestParty(6)}>
              {UI.guestParty6}
            </button>
          </details>
          <label className="cl-weather-toggle">
            <input
              type="checkbox"
              checked={weatherFxOn}
              onChange={(e) => setWeatherFxOn(e.target.checked)}
            />
            {UI.weatherEffects}
          </label>
          <button type="button" className="cl-btn cl-btn-sidebar" onClick={() => void loadWeather({ force: true }).then(setWeather)}>
            {UI.weatherRefresh}
          </button>
          <details className="cl-admin-section" open>
            <summary>{UI.sectionTables}</summary>
          <div className="cl-scenario-block">
            <strong className="cl-scenario-title">Mesas din�micas (A�H)</strong>
            {(Object.keys(SCENARIO_006_META) as Scenario006Id[]).map((id) => (
              <button key={id} type="button" className="cl-btn cl-btn-sidebar cl-btn-scenario" onClick={() => run006(id)}>
                {SCENARIO_006_META[id].title}
              </button>
            ))}
          </div>
          <div className="cl-scenario-block">
            <strong className="cl-scenario-title">Operaci�n integral</strong>
          <button type="button" className="cl-btn cl-btn-sidebar" onClick={reset}>
            {UI.resetDemo}
          </button>
          {Object.keys(INTEGRAL_SCENARIOS).map((key) => (
            <button
              key={key}
              type="button"
              className="cl-btn cl-btn-sidebar"
              onClick={() => runScenario(key)}
            >
              {UI.scenario(key)}
            </button>
          ))}
          </div>
          </details>
        </aside>
      </div>
      <footer className="cl-footer">{renderFocusPanel()}</footer>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
